import type { ChatFileAttachment, EnvironmentId } from "@cmmd-forge/contracts";

import { type ComposerFileAttachment } from "../composerDraftStore";
import { composerFileAttachmentStore } from "../composerFileAttachmentStore";
import { readEnvironmentApi } from "../environmentApi";
import { uploadEnvironmentAttachment } from "./uploadEnvironmentAttachment";
import { syncThreadAttachmentsToCloud } from "./threadAttachmentCloudLane";

/**
 * Hydrates durable file refs and uploads them only during an explicit send.
 *
 * When `cloudMirror` carries the thread id, the same bytes are also mirrored
 * into CMMD object storage (forge#3516) so an attachment outlives the runtime
 * that received it. The mirror is fire-and-forget behind its own flag and
 * starts only once the caller's `accepted` promise resolves, so an aborted or
 * rejected dispatch never leaves a cloud upload running for a message that
 * never sent: the runtime upload above stays the authoritative lane, the
 * caller decides which runs mirror, and a slow or failed mirror must not
 * delay or fail the send.
 */
export async function prepareComposerFileAttachments(input: {
  readonly environmentId: EnvironmentId;
  readonly files: readonly ComposerFileAttachment[];
  readonly signal?: AbortSignal;
  readonly cloudMirror?: {
    readonly threadId: string;
    /**
     * Settles only when the caller's dispatch has accepted the turn. The
     * mirror waits on it; a rejection (abort, send failure) cancels the copy.
     */
    readonly accepted: Promise<unknown>;
  };
}): Promise<readonly ChatFileAttachment[]> {
  const files = await Promise.all(
    input.files.map(async (attachment) => {
      const file = attachment.file ?? (await composerFileAttachmentStore.read(attachment));
      return file;
    }),
  );
  const controller = new AbortController();
  const signal = input.signal
    ? AbortSignal.any([input.signal, controller.signal])
    : controller.signal;
  signal.throwIfAborted();
  const uploaded: ChatFileAttachment[] = [];
  const uploads = files.map(async (file) => {
    const attachment = await uploadEnvironmentAttachment({
      environmentId: input.environmentId,
      file,
      signal,
    });
    uploaded.push(attachment);
    return { file, attachment };
  });
  try {
    const uploadedPairs = await Promise.all(uploads);
    if (input.cloudMirror) {
      const { threadId, accepted } = input.cloudMirror;
      const mirrorFiles = uploadedPairs.map(({ file, attachment }) => ({
        file,
        attachmentId: attachment.id,
      }));
      // Gated on the send: a resolved `accepted` starts the fire-and-forget
      // mirror, a rejected one cancels it before any request is made.
      void accepted.then(
        () => syncThreadAttachmentsToCloud({ threadId, files: mirrorFiles, signal }),
        () => undefined,
      );
    }
    return uploadedPairs.map(({ attachment }) => attachment);
  } catch (error) {
    controller.abort();
    await Promise.allSettled(uploads);
    let api: ReturnType<typeof readEnvironmentApi>;
    try {
      api = readEnvironmentApi(input.environmentId);
    } catch {
      api = undefined;
    }
    if (api) {
      await Promise.all(
        uploaded.map((attachment) => api.attachments.delete({ attachmentId: attachment.id })),
      ).catch(() => undefined);
    }
    throw error;
  }
}
